import React from 'react';
import { Card, CardActionArea, CardContent, CardMedia, Typography, Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from "../../theme";
import { hoverLift, imageZoom, eyebrow } from "../../styles/surfaces";

const Project = ({ project, onClick }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const mediaHeight = { xs: 250, sm: 220, md: 210 };

    return (
        <Card
            sx={{
                height: '100%',
                backgroundColor: colors.background[300],
                ...hoverLift(theme)
            }}
        >
            {/* The click handler used to sit on the Card itself, which gave the card no
                role, no tab stop and no key handling — the modal could be opened with a
                mouse and by no other means. CardActionArea is a button underneath, so it
                brings focus, Enter/Space and the focus ring with it. */}
            <CardActionArea
                onClick={onClick}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    // The action area's own hover tint would double up with the card lift.
                    '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
                }}
            >
                <Box sx={{ position: 'relative', width: '100%', height: mediaHeight, ...imageZoom }}>
                    {project.imgSrc ? (
                        <CardMedia
                            component="img"
                            image={project.imgSrc}
                            alt={project.imgAlt}
                            loading="lazy"
                            sx={{ height: '100%', width: '100%' }}
                        />
                    ) : (
                        // Fallback panel for projects with no photography available yet
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 3,
                                textAlign: 'center',
                                backgroundColor: colors.background[500],
                                backgroundImage: `linear-gradient(135deg, ${colors.background[500]} 0%, ${colors.background[400]} 100%)`
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                {project.title}
                            </Typography>
                        </Box>
                    )}

                    {/* Sector reads as a label on the image rather than as a third item in
                        the metadata line below, where it was competing with the location. */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                            backgroundColor: alpha('#0b1018', 0.62),
                            border: `1px solid ${alpha('#ffffff', 0.18)}`,
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                ...eyebrow(theme),
                                mb: 0,
                                fontSize: '0.6875rem',
                                color: '#ffffff',
                            }}
                        >
                            {project.sector}
                        </Typography>
                    </Box>
                </Box>

                <CardContent sx={{ p: { xs: 3, md: 3.5 }, width: '100%' }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ color: theme.palette.text.primary }}
                    >
                        {project.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            mb: 1.5,
                        }}
                    >
                        {project.location}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '1rem',
                            lineHeight: 1.6
                        }}
                    >
                        {project.description}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default Project;
